import { useMemo } from 'react'

import { countByCategory, uniqueCategories } from '@/common/app/modules/media/citation'
import { CitationItem } from '@/common/model/media/citation'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import SortButton from '@/components/view/atoms/button/SortButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

import GeoSection from '@/components/container/modules/mediaGeo/common/GeoSection'

interface Props {
  citations: CitationItem[]
  // ジャンルチャートからの選択カテゴリと同期
  selectedCategory: string
  onSelectCategory(category: string): void
}

// 言及元ページ一覧（カテゴリタブで絞り込み）
const CitationPageList = ({ citations, selectedCategory, onSelectCategory }: Props) => {
  const categories = useMemo(() => uniqueCategories(citations), [citations])
  const categoryCounts = useMemo(() => countByCategory(citations), [citations])
  const filtered = selectedCategory
    ? citations.filter((c) => c.category === selectedCategory)
    : citations

  return (
    <GeoSection title='言及元ページ' subText='ブランドが言及されているページ' noPadding>
      {/* カテゴリ絞り込み */}
      <div className='flex flex-wrap gap-2 p-3'>
        <SortButton
          quantity={citations.length}
          selected={selectedCategory === ''}
          noPulldown
          onClick={() => onSelectCategory('')}
        >
          すべて
        </SortButton>
        {categories.map((category) => (
          <SortButton
            key={category}
            quantity={categoryCounts[category] || 0}
            selected={selectedCategory === category}
            noPulldown
            onClick={() => onSelectCategory(selectedCategory === category ? '' : category)}
          >
            {category}
          </SortButton>
        ))}
      </div>

      <table className='w-full border-collapse'>
        <thead>
          <TableRow head>
            <TableHeadItem type='thin'>
              <Text size={12} color={TextColor.SECONDARY}>
                タイトル / URL
              </Text>
            </TableHeadItem>
            <TableHeadItem type='thin' width={160}>
              <Text size={12} color={TextColor.SECONDARY}>
                カテゴリ
              </Text>
            </TableHeadItem>
            <TableHeadItem type='thin' width={160}>
              <Text size={12} color={TextColor.SECONDARY}>
                ブランド
              </Text>
            </TableHeadItem>
          </TableRow>
        </thead>
        <tbody>
          {filtered.map((citation, i) => (
            <TableRow key={i}>
              <TableRowItem align='middle'>
                <div className='flex min-w-0 flex-col gap-0.5'>
                  <TextButton size={14} link={citation.url} newTab truncate>
                    {citation.title}
                  </TextButton>
                  <Text size={12} color={TextColor.SECONDARY} truncate>
                    {citation.url}
                  </Text>
                </div>
              </TableRowItem>
              <TableRowItem width={160} align='middle'>
                <Badge size={12} badgeType={BadgeType.SQUARE_SECONDARY}>
                  {citation.category}
                </Badge>
              </TableRowItem>
              <TableRowItem width={160} align='middle'>
                <Text size={14}>{citation.brand}</Text>
              </TableRowItem>
            </TableRow>
          ))}
        </tbody>
      </table>
    </GeoSection>
  )
}

export default CitationPageList
